import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { OverlayPanel } from "primereact/overlaypanel";
import { HttpRequestStatus, LoadingContainer } from "./loadingContainer";
import { Dropdown } from "primereact/dropdown";
import { ReportingCycleEnum } from "../api/models/reportingCycle.models";
import useReportingCycle from "../hooks/useReportingCycle";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import useCreateZeroReport from "../hooks/useCreateZeroReport";
import { ReportStatus, ReportType } from "../api/models/report.models";
import { InputTextarea } from "primereact/inputtextarea";

const TypeSelectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: fit-content;
`;

const TypeSelectionDiv = styled.div<{ $disabled?: boolean }>`
  width: 900px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 10px;
  padding: 20px 10px;
  cursor: pointer;
  color: ${(props) =>
    props.$disabled ? "var(--gray-200)" : "var(--sumi-dark-blue)"};
  border: ${(props) =>
    props.$disabled
      ? "1px solid var(--gray-200)"
      : "1px solid var(--sumi-light-blue)"};
  background-color: : "white";

  &:hover {
    border: ${(props) =>
      props.$disabled
        ? "1px solid var(--gray-200)"
        : "2px solid var(--sumi-blue)"};
    padding: ${(props) => (props.$disabled ? "20px 10px" : "19px 9px")};
  }

  &:focus {
    border: ${(props) =>
      props.$disabled
        ? "1px solid var(--gray-200)"
        : "2px solid var(--sumi-blue)"};
    padding: ${(props) => (props.$disabled ? "20px 10px" : "19px 9px")};
  }

  & .type-icon {
    color: ${(props) =>
      props.$disabled ? "var(--gray-200)" : "var(--sumi-dark-blue)"};
  }
`;

const TypeIcon = styled.i`
  font-size: 3rem;
  color: var(--sumi-dark-blue);
  margin: auto 20px;
`;

const TypeText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const TypeHeader = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  flex: 1;
  margin-bottom: 0;
`;

const TypeLinks = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;

  & .additional-info-link {
    color: var(--sumi-blue);
    cursor: pointer;
    text-decoration: underline;
  }
`;

const TextOverlay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
  width: 400px;

  & h4 {
    margin-bottom: 0;
    font-size: 1.5rem;
    font-weight: 700;
  }
`;

const YearSelectorContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  margin-bottom: 1rem;
`;

const ReportExistsNotification = styled.div`
  background-color: var(--yellow-50);
  border-left: 4px solid var(--yellow-500);
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  color: var(--text-color);

  & i {
    margin-right: 0.5rem;
    color: var(--yellow-600);
  }
`;

const NoReportingCycleWarning = styled.div`
  background-color: var(--red-50);
  border-left: 4px solid var(--red-500);
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  color: var(--text-color);

  & i {
    margin-right: 0.5rem;
    color: var(--red-600);
  }

  & a {
    color: var(--sumi-blue);
    text-decoration: underline;
    cursor: pointer;
  }
`;

const NoReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;

  & .no-report-checkbox-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 20px;
    margin-left: auto;
  }

  & .p-inputtextarea {
    width: 100%;
    padding-bottom: 24px;
  }

  & .p-button {
    margin-left: auto;
  }
`;

const TextareaWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const CharacterCount = styled.div`
  margin-left: auto;
  font-size: 12px;
  color: var(--sumi-text-secondary);
  pointer-events: none;
  margin-top: 4px;
`;

interface ChooseReportingTypeProps {
  handleSelect: (type: ReportType) => void;
  isCreatingNewReport: boolean;
  eanReportAlreadyExists: boolean;
  lightOrDetailedReportAlreadyExists: boolean;
}

const ChooseReportingType: React.FC<ChooseReportingTypeProps> = ({
  handleSelect,
  isCreatingNewReport,
  eanReportAlreadyExists,
  lightOrDetailedReportAlreadyExists,
}: ChooseReportingTypeProps) => {
  const { t } = useTranslation();
  const [nothingToReport, setNothingToReport] = React.useState<boolean>(false);
  const [noReportComment, setNoReportComment] = React.useState<string>("");
  const [showRequiredWarning, setShowRequiredWarning] =
    React.useState<boolean>(false);
  const maxLength = 6000;

  const [
    companyId,
    organizationId,
    selectedYear,
    selectedQuarter,
    selectedMonth,
    setSelectedYear,
    setSelectedQuarter,
    setSelectedMonth,
    toast,
  ] = useUIStore(
    useShallow((state: UIState) => [
      state.companyId,
      state.organizationId,
      state.selectedYearForNewReport,
      state.selectedQuarterForNewReport,
      state.selectedMonthForNewReport,
      state.setSelectedYearForNewReport,
      state.setSelectedQuarterForNewReport,
      state.setSelectedMonthForNewReport,
      state.toastRef,
    ]),
  );

  const { createNewReport, isCreatingReport } = useCreateZeroReport();

  const opexcel = useRef<any>(null);
  const opean = useRef<any>(null);

  const { reportingCycleData, isLoadingReportingCycle } = useReportingCycle({
    companyId: companyId as number,
    enabled: !!companyId,
  });

  const selectedReportingCycle = reportingCycleData?.find(
    (cycle) => cycle.year === selectedYear,
  );

  const reportingCycle = selectedReportingCycle
    ? selectedReportingCycle.cycle
    : ReportingCycleEnum.YEARLY;

  const generateYearOptions = () => {
    return (
      reportingCycleData
        ?.filter(
          (cycle) =>
            cycle.year === 2025 ||
            ((cycle.cycle === ReportingCycleEnum.MONTHLY ||
              cycle.cycle === ReportingCycleEnum.QUARTERLY) &&
              cycle.year === 2026),
        )
        .map((cycle) => ({
          label: cycle.year.toString(),
          value: cycle.year,
        })) || []
    );
  };

  // Creates zero report for the given year and quarter and directs user to reporting page with sent reports shown
  const handleCreateZeroReport = async () => {
    if (nothingToReport && noReportComment.trim() === "") {
      setShowRequiredWarning(true);
      return;
    } else {
      setShowRequiredWarning(false);
    }

    if (!selectedYear) {
      toast?.current?.show({
        severity: "error",
        detail: t("error-no-year-selected-detail"),
        life: 5000,
      });
      return;
    }

    await createNewReport({
      companyId: companyId as number,
      organizationId: organizationId || undefined,
      year: selectedYear as number,
      quarter:
        reportingCycle === ReportingCycleEnum.QUARTERLY
          ? (selectedQuarter as 1 | 2 | 3 | 4)
          : undefined,
      month:
        reportingCycle === ReportingCycleEnum.MONTHLY
          ? (selectedMonth as number)
          : undefined,
      reportStatus: ReportStatus.Sent,
      comment: noReportComment,
      reportType: ReportType.EAN,
    });
  };

  const selectionDisabled = (reportType: ReportType) => {
    if (reportType === ReportType.EAN && eanReportAlreadyExists) {
      return true;
    }
    if (
      reportType === ReportType.DETAILED &&
      lightOrDetailedReportAlreadyExists
    ) {
      return true;
    }

    if (reportingCycle === ReportingCycleEnum.MONTHLY && !selectedMonth) {
      return true;
    }
    if (reportingCycle === ReportingCycleEnum.QUARTERLY && !selectedQuarter) {
      return true;
    }
    if (reportingCycle === ReportingCycleEnum.YEARLY && !selectedYear) {
      return true;
    }
    return false;
  };

  const handleSelectionClick = (type: ReportType) => {
    // Ensure all required selections are made before proceeding
    if (selectionDisabled(type)) {
      return;
    }

    handleSelect(type);
  };

  return (
    <TypeSelectionContainer>
      <LoadingContainer
        status={
          isCreatingNewReport || isLoadingReportingCycle
            ? HttpRequestStatus.LOADING
            : HttpRequestStatus.SUCCESS
        }
      >
        <YearSelectorContainer>
          <Dropdown
            value={selectedYear}
            options={generateYearOptions()}
            onChange={(e) => setSelectedYear?.(e.value)}
            placeholder={t("placeholder-select-year")}
          />
          {reportingCycle === ReportingCycleEnum.QUARTERLY && (
            <Dropdown
              value={selectedQuarter}
              options={[
                { label: "Q1", value: 1 },
                { label: "Q2", value: 2 },
                { label: "Q3", value: 3 },
                { label: "Q4", value: 4 },
              ]}
              onChange={(e) => setSelectedQuarter?.(e.value)}
            />
          )}
          {reportingCycle === ReportingCycleEnum.MONTHLY && (
            <Dropdown
              value={selectedMonth}
              options={[
                { label: t("month-january"), value: 1 },
                { label: t("month-february"), value: 2 },
                { label: t("month-march"), value: 3 },
                { label: t("month-april"), value: 4 },
                { label: t("month-may"), value: 5 },
                { label: t("month-june"), value: 6 },
                { label: t("month-july"), value: 7 },
                { label: t("month-august"), value: 8 },
                { label: t("month-september"), value: 9 },
                { label: t("month-october"), value: 10 },
                { label: t("month-november"), value: 11 },
                { label: t("month-december"), value: 12 },
              ]}
              style={{ minWidth: "150px" }}
              onChange={(e) => setSelectedMonth?.(e.value)}
            />
          )}
        </YearSelectorContainer>

        {(!reportingCycleData || reportingCycleData.length === 0) && (
          <NoReportingCycleWarning>
            <i className="pi pi-exclamation-triangle" />
            {t("warning-no-reporting-cycle")}{" "}
            <a href="/organizations">{t("link-organizations-page")}</a>
          </NoReportingCycleWarning>
        )}

        {eanReportAlreadyExists && lightOrDetailedReportAlreadyExists && (
          <ReportExistsNotification>
            <i className="pi pi-info-circle" />
            {t("notification-report-already-exists")}
          </ReportExistsNotification>
        )}

        <TypeSelectionDiv
          role="button"
          tabIndex={0}
          className="type-selection"
          onClick={() => handleSelectionClick(ReportType.EAN)}
          $disabled={selectionDisabled(ReportType.EAN)}
        >
          <TypeIcon className="type-icon pi pi-barcode" />
          <TypeText className="type-text">
            <TypeHeader>{t("ean-gtin-report-header")}</TypeHeader>
            <TypeLinks>
              <span>{t("label-short-description-ean")}</span>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  opean.current?.toggle(e);
                  opexcel.current?.hide();
                }}
                className="additional-info-link"
              >
                {t("label-additional-info")}
              </span>
              <OverlayPanel ref={opean}>
                <TextOverlay>
                  <h4>EAN/GTIN-raportointi</h4>
                  <span>{t("text-ean-gtin-info-overlay")}</span>
                </TextOverlay>
              </OverlayPanel>
            </TypeLinks>
          </TypeText>
        </TypeSelectionDiv>

        <TypeSelectionDiv
          role="button"
          tabIndex={0}
          className="type-selection"
          onClick={() => handleSelectionClick(ReportType.DETAILED)}
          $disabled={selectionDisabled(ReportType.DETAILED)}
        >
          <TypeIcon className="type-icon pi pi-file-excel" />
          <TypeText className="type-text">
            <TypeHeader>{t("header-traditional-reporting")}</TypeHeader>
            <TypeLinks>
              <span>{t("label-short-description-traditional-reporting")}</span>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  opexcel.current?.toggle(e);
                  opean.current?.hide();
                }}
                className="additional-info-link"
              >
                {t("label-additional-info")}
              </span>
              <OverlayPanel ref={opexcel}>
                <TextOverlay>
                  <h4>{t("header-traditional-reporting")}</h4>
                  <span>{t("text-traditional-reporting-info-overlay")}</span>
                </TextOverlay>
              </OverlayPanel>
            </TypeLinks>
          </TypeText>
        </TypeSelectionDiv>

        <Divider />

        <NoReportContainer>
          <div className="no-report-checkbox-container">
            <label
              htmlFor="no-report-checkbox"
              style={{
                color:
                  lightOrDetailedReportAlreadyExists || eanReportAlreadyExists
                    ? "var(--gray-200)"
                    : "var(--sumi-dark-blue)",
              }}
            >
              {t("label-no-report-this-year")}
            </label>
            <Checkbox
              id="no-report-checkbox"
              checked={nothingToReport}
              onChange={(e) => setNothingToReport(!!e.checked)}
              disabled={
                eanReportAlreadyExists || lightOrDetailedReportAlreadyExists
              }
            />
          </div>

          <TextareaWrapper>
            <InputTextarea
              value={noReportComment}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= maxLength) {
                  setNoReportComment(value);
                }
              }}
              placeholder={t("placeholder-no-report-comment")}
              rows={3}
              cols={50}
              disabled={!nothingToReport}
              maxLength={maxLength}
            />
            <CharacterCount>
              {noReportComment.length} / {maxLength}
            </CharacterCount>
          </TextareaWrapper>

          {showRequiredWarning && (
            <div
              style={{
                color: "var(--sumi-error-color)",
                fontSize: "0.9rem",
                marginTop: "-10px",
              }}
            >
              {t("warning-no-report-comment-required")}
            </div>
          )}

          <Button
            id="confirm-no-report-button"
            disabled={
              !nothingToReport ||
              isCreatingReport ||
              eanReportAlreadyExists ||
              lightOrDetailedReportAlreadyExists
            }
            onClick={handleCreateZeroReport}
            loading={isCreatingReport}
            label={t("button-confirm-no-report")}
          />
        </NoReportContainer>
      </LoadingContainer>
    </TypeSelectionContainer>
  );
};

export default ChooseReportingType;
